import { useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Search, Users, ListChecks, CreditCard, UserCog, RefreshCw } from "lucide-react";
import { cn } from "@/lib/cn";
import { useClients } from "@/features/clients/clients.hooks";
import { useTasks } from "@/features/tasks/tasks.hooks";
import { usePayments } from "@/features/payments/payments.hooks";
import { getPaymentDetailPath } from "@/features/payments/payment-details.logic";
import { useTeamMembers } from "@/features/team/team.hooks";
import { useSubscriptions } from "@/features/subscriptions/subscriptions.hooks";

type ResultGroup = "clients" | "tasks" | "payments" | "team" | "subscriptions";

interface SearchResult {
  group: ResultGroup;
  id: string;
  label: string;
  sublabel?: string;
  to: string;
}

const GROUP_ICON: Record<ResultGroup, typeof Search> = {
  clients: Users,
  tasks: ListChecks,
  payments: CreditCard,
  team: UserCog,
  subscriptions: RefreshCw,
};

const GROUP_ORDER: ResultGroup[] = ["clients", "tasks", "payments", "team", "subscriptions"];
const MAX_PER_GROUP = 5;

function matches(query: string, ...fields: (string | undefined | null)[]): boolean {
  return fields.some((f) => f && f.toLowerCase().includes(query));
}

/** Frontend-only global search across the loaded entities (respects mock fallback). */
export function GlobalSearch() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);

  const { clients } = useClients();
  const { tasks } = useTasks();
  const { payments } = usePayments();
  const { members } = useTeamMembers();
  const { subscriptions } = useSubscriptions();

  const results = useMemo<SearchResult[]>(() => {
    const q = query.trim().toLowerCase();
    if (q.length < 1) return [];
    const out: SearchResult[] = [];

    for (const c of clients.filter((c) => matches(q, c.name, c.email)).slice(0, MAX_PER_GROUP)) {
      out.push({ group: "clients", id: c.id, label: c.name, sublabel: c.email, to: `/clients/${c.id}` });
    }
    for (const tk of tasks.filter((tk) => matches(q, tk.title, tk.clientName, tk.assignee)).slice(0, MAX_PER_GROUP)) {
      out.push({ group: "tasks", id: tk.id, label: tk.title, sublabel: tk.clientName, to: "/tasks" });
    }
    for (const p of payments.filter((p) => matches(q, p.clientName, p.note, p.period)).slice(0, MAX_PER_GROUP)) {
      out.push({ group: "payments", id: p.id, label: p.clientName, sublabel: p.note ?? p.period, to: getPaymentDetailPath(p.id) });
    }
    for (const m of members.filter((m) => matches(q, m.name, m.email, m.role)).slice(0, MAX_PER_GROUP)) {
      out.push({ group: "team", id: m.id, label: m.name, sublabel: m.email, to: "/team" });
    }
    for (const s of subscriptions.filter((s) => matches(q, s.name, s.provider)).slice(0, MAX_PER_GROUP)) {
      out.push({ group: "subscriptions", id: s.id, label: s.name, sublabel: s.provider, to: "/subscriptions" });
    }
    return out;
  }, [query, clients, tasks, payments, members, subscriptions]);

  const grouped = useMemo(() => {
    const map = new Map<ResultGroup, SearchResult[]>();
    for (const r of results) {
      const arr = map.get(r.group) ?? [];
      arr.push(r);
      map.set(r.group, arr);
    }
    return map;
  }, [results]);

  const go = (r: SearchResult) => {
    navigate(r.to);
    setQuery("");
    setOpen(false);
  };

  const showDropdown = open && query.trim().length >= 1;

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Escape") {
      setOpen(false);
      (e.target as HTMLInputElement).blur();
      return;
    }
    if (!showDropdown || results.length === 0) {
      if (e.key === "ArrowDown") setOpen(true);
      return;
    }
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActive((i) => (i + 1) % results.length);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActive((i) => (i - 1 + results.length) % results.length);
    } else if (e.key === "Enter") {
      e.preventDefault();
      const r = results[active] ?? results[0];
      if (r) go(r);
    }
  };

  // Flatten index → used to highlight the active row across groups.
  let flatIndex = -1;

  return (
    <div
      ref={containerRef}
      className="relative flex-1 max-w-[360px]"
      onBlur={(e) => {
        if (!containerRef.current?.contains(e.relatedTarget as Node)) setOpen(false);
      }}
    >
      <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3 w-3 text-text3 pointer-events-none" />
      <input
        type="text"
        aria-label={t("topbar.search")}
        placeholder={t("search.placeholder")}
        value={query}
        onChange={(e) => {
          setQuery(e.target.value);
          setActive(0);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        onKeyDown={onKeyDown}
        className="w-full h-9 pl-8 pr-3 bg-bg3 border border-border rounded-sm text-xs text-text outline-none transition-all placeholder:text-text3 focus:border-primary focus:bg-bg4"
      />

      {showDropdown && (
        <div className="absolute left-0 right-0 top-[calc(100%+6px)] z-[60] max-h-[60vh] overflow-y-auto scroll-thin rounded-md border border-border2 bg-card shadow-floating animate-fadeIn">
          {results.length === 0 ? (
            <div className="px-4 py-6 text-center text-xs text-text3">{t("search.noResults")}</div>
          ) : (
            GROUP_ORDER.filter((g) => grouped.has(g)).map((group) => {
              const Icon = GROUP_ICON[group];
              return (
                <div key={group} className="py-1.5">
                  <div className="px-3 pt-1 pb-1 text-[9.5px] font-bold uppercase tracking-[0.1em] text-text3">
                    {t(`search.groups.${group}`)}
                  </div>
                  {grouped.get(group)!.map((r) => {
                    flatIndex += 1;
                    const isActive = flatIndex === active;
                    return (
                      <button
                        key={`${r.group}-${r.id}`}
                        type="button"
                        onMouseEnter={() => setActive(results.indexOf(r))}
                        onClick={() => go(r)}
                        className={cn(
                          "flex w-full items-center gap-2.5 px-3 py-2 text-left transition-colors",
                          isActive ? "bg-primary-dim" : "hover:bg-white/[0.04]",
                        )}
                      >
                        <Icon className={cn("h-3.5 w-3.5 shrink-0", isActive ? "text-primary" : "text-text3")} />
                        <span className="min-w-0 flex-1">
                          <span className="block truncate text-[12.5px] font-medium text-text">{r.label}</span>
                          {r.sublabel && (
                            <span className="block truncate text-[10.5px] text-text3">{r.sublabel}</span>
                          )}
                        </span>
                      </button>
                    );
                  })}
                </div>
              );
            })
          )}
        </div>
      )}
    </div>
  );
}
